import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Success = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [orderId, setOrderId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId || !user || user === false) {
      setStatus('error');
      return;
    }
    checkPaymentStatus();
  }, [sessionId, user]);

  const checkPaymentStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true,
      });

      if (data.status === 'paid') {
        setStatus('success');
        setOrderId(data.order_id);
      } else if (attempts < 5) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < 5) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-sm border border-border p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              Vérification du paiement...
            </h2>
            <p className="text-muted-foreground">Veuillez patienter</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              Paiement réussi !
            </h2>
            <p className="text-muted-foreground mb-6">
              Merci pour votre commande. Votre commande a été confirmée et sera traitée dans les plus
              brefs délais.
            </p>
            {orderId && (
              <p className="text-sm text-muted-foreground mb-6">
                Numéro de commande : <span className="font-mono font-semibold">{orderId.substring(0, 8)}</span>
              </p>
            )}
            <div className="space-y-3">
              <Link
                to="/account"
                className="block w-full btn-primary py-3 rounded-sm font-semibold"
                data-testid="view-orders-link"
              >
                Voir mes commandes
              </Link>
              <Link
                to="/products"
                className="block w-full btn-secondary py-3 rounded-sm font-semibold"
                data-testid="continue-shopping-link"
              >
                Continuer mes achats
              </Link>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="text-4xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              Paiement en cours
            </h2>
            <p className="text-muted-foreground mb-6">
              Votre paiement est en cours de traitement. Vérifiez vos emails pour la confirmation.
            </p>
            <Link
              to="/account"
              className="inline-block btn-primary px-6 py-3 rounded-sm font-semibold"
            >
              Voir mes commandes
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-4xl mb-4">❌</div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
              Erreur
            </h2>
            <p className="text-muted-foreground mb-6">
              Une erreur est survenue lors de la vérification du paiement.
            </p>
            <Link
              to="/cart"
              className="inline-block btn-primary px-6 py-3 rounded-sm font-semibold"
            >
              Retour au panier
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Success;